import React, { ComponentType, useState } from 'react';
import { ArticleRounded, AttachFileRounded, CheckroomRounded, ExpandMoreRounded, FolderOpenRounded, ManageAccountsRounded, MedicalServicesRounded, MosqueRounded, PeopleRounded, ReceiptLongRounded, ReceiptRounded, VillaRounded, WorkHistoryRounded } from "@mui/icons-material";
import { Drawer, Toolbar, List, ListItemButton, ListItemIcon, ListItemText, Collapse, styled, Typography, useTheme, ListItem, Theme, SxProps, AppBar } from "@mui/material";
import LinkBehavior from './LinkBehavior';
import { useLocation } from 'react-router-dom';
import { blue, grey } from '@mui/material/colors';
import { useAppSelector } from '../store';
import { ROLES_GROUP } from '../security/Role';
import { SecureComponent } from '../security/SecureComponent';
import { PERMISSIONS_ARTICLES, PERMISSIONS_USERS } from '../security/Permission';

const ExpandMoreIndicator = styled(ExpandMoreRounded, {
  shouldForwardProp: (prop) => prop !== 'open' 
})<{open: boolean}>(({ open, theme }) => ({
  transition: theme.transitions.create(['transform'], {
    duration: theme.transitions.duration.short,
  }),
  transform: open ? 'rotate(0)' : 'rotate(-90deg)',
}));

interface SideListItemProps {
  to: string,
  text: string,
  location: string,
  Icon: ComponentType<any>
};
const SideListItem = (props: SideListItemProps) => {
  const theme = useTheme();
  const { to, text, location, Icon } = props;

  const ListItemSx: SxProps<Theme> = {
    borderTopRightRadius: 24,
    borderBottomRightRadius: 24,
  };

  const selected = location.startsWith(to);

  return (
    <ListItem sx={ListItemSx} button selected={selected} component={LinkBehavior} href={to}>
      <ListItemIcon>
        <Icon sx={{ color: selected ? theme.palette.primary.main : theme.palette.primary.light}} />
      </ListItemIcon>
      <ListItemText primary={text} />
    </ListItem>
  );
};

export const MyDrawer = () => {
  const theme = useTheme();
  const location = useLocation();
  const [homeOpen, setHomeOpen] = useState(true);
  const [servicesOpen, setServicesOpen] = useState(true);
  const [dashboardOpen, setDashboardOpen] = useState(true);
  const [adminOpen, setAdminOpen] = useState(true);
  const authState = useAppSelector(s => s.auth);

  const ListItemSx: SxProps<Theme> = {
    borderTopRightRadius: 24,
    borderBottomRightRadius: 24,
  };

  const handleHomeOnClick = () => {
    setHomeOpen(!homeOpen);
  }

  const handleServicesOnClick = () => {
    setServicesOpen(!servicesOpen);
  }

  const handleDashboardOnClick = () => {
    setDashboardOpen(!dashboardOpen);
  }

  const handleAdminOnClick = () => {
    setAdminOpen(!adminOpen);
  }

  return (
    <Drawer
      sx={{
        width: 300,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 300,
          boxSizing: 'border-box',
          borderWidth: 0,
        },
      }}
      variant="permanent"
      anchor="left"
    >
      <AppBar position="sticky" elevation={0}>
        <Toolbar>
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.3rem',
              color: '#fff',
              textDecoration: 'none',
            }}
          >
            NADAFA EMS
          </Typography>
        </Toolbar>
      </AppBar>
      <List
        component="nav"
        sx={{ mt: 1 }}
        subheader={
          <ListItemButton onClick={handleHomeOnClick}>
            <ListItemText>
              <Typography variant='button' color={theme.palette.grey[700]}>HOME</Typography>
            </ListItemText>
            <ExpandMoreIndicator open={homeOpen} />
          </ListItemButton>
        }>
        <Collapse in={homeOpen} timeout="auto" unmountOnExit>
          <SideListItem to="/home/employees" text="Employees" location={location.pathname} Icon={PeopleRounded} />
          <SideListItem to="/home/attendance" text="Attendance" location={location.pathname} Icon={WorkHistoryRounded} />
        </Collapse>
      </List>
      <SecureComponent roles={ROLES_GROUP.SERVICES}>
        <List
          component="nav"
          subheader={
            <ListItemButton onClick={handleServicesOnClick}>
              <ListItemText>
                <Typography variant='button' color={theme.palette.grey[700]}>SERVICES</Typography>
              </ListItemText>
              <ExpandMoreIndicator open={servicesOpen} />
            </ListItemButton>
          }>
          <Collapse in={servicesOpen} timeout="auto" unmountOnExit>
            <SecureComponent roles={ROLES_GROUP.VACATION}>
              <SideListItem to="/services/vacationRequests" text="Vacation Requests" location={location.pathname} Icon={VillaRounded} />
            </SecureComponent>
            <SecureComponent roles={ROLES_GROUP.IQAMA}>
              <SideListItem to="/services/iqamaRenewalReceipt" text="Iqama Renewal Receipt" location={location.pathname} Icon={ReceiptRounded} />
            </SecureComponent>
            <SecureComponent roles={ROLES_GROUP.TASHERA}>
              <SideListItem to="/services/vacationReceipt" text="Tashera Payments" location={location.pathname} Icon={ReceiptLongRounded} />
            </SecureComponent>
            <SecureComponent roles={ROLES_GROUP.OTHER_FILES}>
              <SideListItem to="/services/otherFiles" text="Other Files" location={location.pathname} Icon={AttachFileRounded} />
            </SecureComponent>
          </Collapse>
        </List>
      </SecureComponent>
      <SecureComponent roles={ROLES_GROUP.DASHBOARD}>
        <List
          component="nav"
          subheader={
            <ListItemButton onClick={handleDashboardOnClick}>
              <ListItemText>
                <Typography variant='button' color={theme.palette.grey[700]}>DASHBOARD</Typography>
              </ListItemText>
              <ExpandMoreIndicator open={dashboardOpen} />
            </ListItemButton>
          }>
          <Collapse in={dashboardOpen} timeout="auto" unmountOnExit>
            <SecureComponent roles={ROLES_GROUP.PASSPORT}>
            <SideListItem to="/dashboard/passportRequests" text="Passport Requests" location={location.pathname} Icon={FolderOpenRounded} />
            </SecureComponent>
            <SecureComponent roles={ROLES_GROUP.UNIFORM}>
              <SideListItem to="/dashboard/uniformRequests" text="Uniform Requests" location={location.pathname} Icon={CheckroomRounded} />
            </SecureComponent>
            <SecureComponent roles={ROLES_GROUP.MEDICAL}>
              <SideListItem to="/dashboard/medicalRequests" text="Medical Requests" location={location.pathname} Icon={MedicalServicesRounded} />
            </SecureComponent>
            <SecureComponent roles={ROLES_GROUP.UMRAH}>
              <SideListItem to="/dashboard/umrahRequests" text="Umrah Requests" location={location.pathname} Icon={MosqueRounded} />
            </SecureComponent>
          </Collapse>
        </List>
      </SecureComponent>
      <SecureComponent permissions={[...PERMISSIONS_USERS, ...PERMISSIONS_ARTICLES]}>
        <List
          component="nav"
          subheader={
            <ListItemButton onClick={handleAdminOnClick}>
              <ListItemText>
                <Typography variant='button' color={theme.palette.grey[700]}>ADMIN</Typography>
              </ListItemText>
              <ExpandMoreIndicator open={adminOpen} />
            </ListItemButton>
          }>
          <Collapse in={adminOpen} timeout="auto" unmountOnExit>
            <SecureComponent permissions={PERMISSIONS_USERS}>
              <SideListItem to="/admin/manageUsers" text="Manage Users" location={location.pathname} Icon={ManageAccountsRounded} />
            </SecureComponent>
            <SecureComponent permissions={PERMISSIONS_USERS}>
              <SideListItem to="/admin/articles" text="Articles" location={location.pathname} Icon={ArticleRounded} />
            </SecureComponent>
          </Collapse>
        </List>
      </SecureComponent>
    </Drawer>
  );
};